import React from 'react';
import { observer } from 'mobx-react';
import { observable, computed } from 'mobx';
import { isPresent } from '@seedlang/utils';
import styled from '@emotion/styled';
import { Theme } from '@seedlang/constants';
import { AppUI } from '@seedlang/state';
import Modal from 'components/modal';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px;
  line-height: 24px;
  width: 100%;
  height: 100%;
`;

const Button = styled.div`
  background: ${props => props.background};
  color: ${props => props.color};
  width: ${props => props.width};
  margin: ${props => props.margin};
  padding: 5px;
  font-weight: bold;
  cursor: pointer;
  border-radius: 10px;
  height: 40px;
  align-items: center;
  justify-content: center;
  display: flex;
  &:active {
    transform:scale(0.98);
  }
`;

const ButtonWrapper = styled.div`
  font-size: 16px;
  width: 100%;
  margin-top: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  @media only screen and (max-width : 649px) {
    flex-direction: column;
  }
`;

const Text = styled.div`
  text-align: left;
  padding: ${props => props.padding};
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Terms = styled.div`
  margin-top: 10px;
`;

const Title = styled.div`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 10px;
`;

@observer
class MissingAgreement extends React.Component {
  @observable hide = false;
  @observable userGroupId = AppUI.user.currentUserGroup?.id;
  @observable userAgreedToTerms = AppUI.user.currentUserGroup?.userAgreedToTerms;
  @observable userAgreedToEmail = isPresent(AppUI.user.currentUserGroup?.userAgreedToEmail) && AppUI.user.currentUserGroup?.userAgreedToEmail !== 'unset';

  @computed get showModal() {
    const onOnboardingRoute = AppUI.routeStore.routeName.startsWith('onboarding');
    return !this.hide && !onOnboardingRoute && AppUI.siteIsMemrev && this.missingTerms;
  }

  @computed get missingTerms() {
    const missingEmail = !AppUI.site?.enableMailerlite && !this.userAgreedToEmail;
    return AppUI.userIsLoaded && (!this.userAgreedToTerms || missingEmail);
  }
  onClick = (field, value) => {
    this[field] = value;
    const data = {};
    data[field] = value;
    if (field === 'userAgreedToEmail') {
      this.userAgreedToEmail = true;
    }
    if (field === 'userAgreedToTerms') {
      this.userAgreedToTerms = true;
    }
    AppUI.userGroupStore.update({ids: {userGroupId: AppUI.user.currentUserGroup?.id}, data: data}, AppUI.loadUser);
  }

  componentDidUpdate() {
    if (!this.missingTerms) {
      this.hide = true;
    }

    if (this.userGroupId !== AppUI.user.currentUserGroup?.id) {
      this.userGroupId = AppUI.user.currentUserGroup?.id;
      this.userAgreedToTerms = AppUI.user.currentUserGroup?.userAgreedToTerms;
      this.userAgreedToEmail = isPresent(AppUI.user.currentUserGroup?.userAgreedToEmail)
        && AppUI.user.currentUserGroup?.userAgreedToEmail !== 'unset';
    }
  }

  render() {
    if (this.showModal) {
      const memrevTermsUrl = AppUI.site.safeTermsOfServiceUrl;
      const memrevPrivacyUrl = AppUI.site.safePrivacyUrl;
      const hasMemrevLegalLinks = Boolean(memrevTermsUrl || memrevPrivacyUrl);

      return (
        <Modal
          width={AppUI.layout.isMobile ? '90%' : '500px'}
          height='fit-content'
          alignItems='center'
          justifyContent='center'
        >
          {
            this.userAgreedToTerms && !this.userAgreedToEmail && !AppUI.site.enableMailerlite &&
              <Wrapper>
                <Title>
                  Stay Up to Date
                </Title>
                <Text>
                We'd like to send you updates about your membership, new content, and marketing offers for related products. You can unsubscribe anytime!
                </Text>
                <ButtonWrapper>
                  <Button
                    background={Theme.red}
                    color='#FFF'
                    onClick={() => this.onClick('userAgreedToEmail', false)}
                    width='300px'
                  >
                    <i className='fa fa-times' style={{marginRight: '5px'}}/>
                    Don't Send Me Emails
                  </Button>
                  <Button
                    background={Theme.green}
                    color='#FFF'
                    onClick={() => this.onClick('userAgreedToEmail', true)}
                    width='300px'
                  >
                    <i className='fa fa-check' style={{marginRight: '5px'}}/>
                    Send Me Emails
                  </Button>
                </ButtonWrapper>
              </Wrapper>
          }
          {
            !this.userAgreedToTerms &&
              <Wrapper>
                <Title>
                  Privacy and Terms of Service
                </Title>
                <Terms>
                  <span>By continuing, I agree to the </span>
                  {
                    hasMemrevLegalLinks && (
                      <>
                        {memrevTermsUrl && <a className='underline' href={memrevTermsUrl} target='_blank' rel='noopener noreferrer'>Terms</a>}
                        {memrevTermsUrl && memrevPrivacyUrl && <span> & </span>}
                        {memrevPrivacyUrl && <a className='underline' href={memrevPrivacyUrl} target='_blank' rel='noopener noreferrer'>Privacy Policy</a>}
                        <span> of Easy Languages GmbH, and the </span>
                      </>
                    )
                  }
                  <a className='underline' href='https://www.seedlang.com/terms' target='_blank' rel='noopener noreferrer'>Terms</a>
                  <span> & </span>
                  <a className='underline' href='https://www.seedlang.com/privacy' target='_blank' rel='noopener noreferrer'>Privacy Policy</a>
                  <span> of the learning platform Seedlang.</span>
                </Terms>
                <ButtonWrapper>
                  <Button
                    background={AppUI.site.accentColor}
                    color='#FFF'
                    onClick={() => this.onClick('userAgreedToTerms', true)}
                    width='300px'
                    margin='0 5px 0 0'
                  >
                    <i className='fa fa-check' style={{marginRight: '5px'}} />
                    I Agree To Terms
                  </Button>
                </ButtonWrapper>
              </Wrapper>
          }
        </Modal>
      );
    }
    return <span />;
  }
}

export default MissingAgreement;
